/* @flow */

import React, { Component } from 'react';
import compose from 'recompose/compose';
import { connect } from 'react-redux';
import { withRouter } from 'react-router-dom';
import * as PagesActions from 'catalog/actions/pages';
import * as ContentsActions from 'catalog/actions/contents';
import * as PlatformsActions from 'catalog/actions/platforms';
import {
  getPageById,
  getPendingPageById,
  hasContentUpdate,
  getIsUpdating,
} from 'catalog/reducers/pages';
import {
  getCurrentContent,
  getPendingContent,
  getPendingContents,
  getIsCreating as getIsCreatingContent,
} from 'catalog/reducers/contents';
import { getPageWarnings } from 'catalog/reducers/page';
import { getSettings } from 'catalog/reducers/catalog';
import { getPlatforms, getEditionPlatform } from 'catalog/reducers/platforms';
import { Message } from 'semantic-ui-react';
import PageHeader from './components/PageHeader';
import PageForm from './components/PageForm';
import PageWarnings from './components/PageWarnings';
import ConfirmPlatformChange from './components/ConfirmPlatformChange';
import PageArchivedWarning from './components/PageArchivedWarning';
import PendingChangesPrompt
  from 'catalog/screens/shared/components/PendingChangesPrompt';
import { getEditComponent } from 'catalog/plugin';

type Props = {
  page?: CatalogPage,
  pendingPage?: CatalogPage,
  content?: CatalogPageContent,
  pendingContent?: CatalogPageContent,
  pendingContents: List<CatalogPageContent>,
  platform: CatalogPlatform,
  platforms: Array<CatalogPlatform>,
  settings: CatalogSettings,
  hasUpdate: boolean,
  isUpdating: boolean,
  isCreatingContent: boolean,
  serialize: (page: CatalogPage) => void,
  serializeContent: (content: CatalogPageContent) => void,
  cancelPending: (page: CatalogPage, content: CatalogPageContent) => void,
  update: (page: CatalogPage) => void,
  changePlatform: (platform: CatalogPlatform) => void,
  createContent: (page: CatalogPage, platform: CatalogPlatform) => void,
};

type State = {
  needPlatformChangeConfirm?: CatalogPlatform,
};

const styles = {
  container: {
    marginTop: '1rem',
  },
};

const mapStateToProps = (state, { match: { params: { id } } }) => ({
  page: getPageById(state, id),
  pendingPage: getPendingPageById(state, id),
  content: getCurrentContent(state, id),
  pendingContent: getPendingContent(state, id),
  pendingContents: getPendingContents(state, id),
  platform: getEditionPlatform(state),
  platforms: getPlatforms(state),
  settings: getSettings(state),
  hasUpdate: hasContentUpdate(state, id),
  isUpdating: getIsUpdating(state),
  isCreatingContent: getIsCreatingContent(state),
});

class Edit extends Component {
  props: Props;
  state: State;

  constructor(props: Props) {
    super(props);

    this.state = {};
  }

  componentWillReceiveProps(nextProps: Props) {
    const shouldHideModal =
      nextProps.isCreatingContent !== this.props.isCreatingContent &&
      !nextProps.isCreatingContent;

    // Checking content creation has finished to hide modal
    if (shouldHideModal) {
      this.setState({
        needPlatformChangeConfirm: undefined,
      });
    }
  }

  componentWillUnmount() {
    const { page, content, hasUpdate, cancelPending } = this.props;

    // Don't forget to clear pending changes when leaving
    if (hasUpdate) {
      cancelPending(page, content);
    }
  }

  renderContent() {
    const { page, pendingContent, serializeContent } = this.props;

    const templateContent = getEditComponent(
      page.template.code,
      pendingContent
    );
    if (templateContent) {
      return React.cloneElement(templateContent, {
        onSerialize: serializeContent,
      });
    }

    console.warn(
      `Cannot render content as template type ${page.template.code} is unknown.`
    );
    return null;
  }

  changePlatform = (platform: CatalogPlatform) => {
    const { changePlatform, pendingContents } = this.props;

    // Checking a pending content already exists for this platform, otherwise requesting confirmation
    if (pendingContents.find(c => c.platform === platform.code)) {
      changePlatform(platform);
    } else {
      this.setState({
        needPlatformChangeConfirm: platform,
      });
    }
  };

  cancelChangePlatform = () =>
    this.setState({ needPlatformChangeConfirm: undefined });

  confirmPlatformChange = () => {
    const { page, createContent } = this.props;
    const { needPlatformChangeConfirm } = this.state;

    // Creating platform content remotely
    createContent(page, needPlatformChangeConfirm);
  };

  changeLayout = (layout: CatalogLayout) => {
    const { page, content, serializeContent, update } = this.props;

    // Serializing content with layout
    serializeContent({
      ...content,
      layout,
    });

    // The saving page
    update(page);
  };

  render() {
    const {
      page,
      pendingPage,
      pendingContent,
      platform,
      platforms,
      settings,
      hasUpdate,
      isUpdating,
      isCreatingContent,
      serialize,
      update,
    } = this.props;

    const { needPlatformChangeConfirm } = this.state;

    if (page) {
      const updatePage = () => update(page);

      return (
        <div style={styles.container}>
          <PageHeader
            page={page}
            platform={platform}
            platforms={platforms}
            isDisabled={isCreatingContent || !hasUpdate}
            isUpdating={isUpdating}
            onUpdate={updatePage}
            onChangePlatform={this.changePlatform}
          />
          <PageForm
            page={pendingPage}
            content={pendingContent}
            hasUpdate={hasUpdate}
            isUpdating={isUpdating}
            settings={settings}
            onChange={serialize}
            onChangeLayout={this.changeLayout}
          />
          <PageArchivedWarning visible={page.isArchived} />
          <PageWarnings warnings={getPageWarnings(page, pendingContent)} />
          {this.renderContent()}
          <ConfirmPlatformChange
            loading={isCreatingContent}
            platform={needPlatformChangeConfirm}
            onConfirm={this.confirmPlatformChange}
            onClose={this.cancelChangePlatform}
          />
          <PendingChangesPrompt visible={hasUpdate} />
        </div>
      );
    }

    return (
      <Message
        attached="bottom"
        icon="help"
        header={`La page demandée n'existe pas`}
      />
    );
  }
}

export default compose(
  connect(mapStateToProps, {
    serialize: PagesActions.serializePage,
    serializeContent: ContentsActions.serializeContent,
    cancelPending: PagesActions.cancelPending,
    update: PagesActions.updatePage,
    changePlatform: PlatformsActions.changeEditionPlatform,
    createContent: ContentsActions.createContent,
  }),
  withRouter
)(Edit);



// WEBPACK FOOTER //
// ./src/modules/catalog/screens/Page/Edit/index.js